import { Button, Message, Text, Theme, useIsTheme, usePhoneNumber } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { Country } from '@afterpaytouch/portal-api/types'
import { ConsumerAddress } from '@afterpay/types'
import { Route } from '../../config/router'
import { isValidObject } from '@afterpaytouch/form/lib/utils/object'
import { FieldAnalyticsLabels, SignupFormAnalyticsFields } from './constants'
import { Disclaimer } from '../Disclaimer/Disclaimer'
import { useSignUpTerms } from '../Disclaimer/hooks'
import FormBuilder from '@afterpaytouch/form'
import { getSchema } from './utils'

import { compareObjects } from '../../utils/comparison'
import { handleAccountDetailsErrors } from '../ProfileForm/helper'
import { useRouter } from 'next/router'
import { LaunchDarklyFlags, useFlag, usePostRegistrationRedirect } from '../../hooks'
import { useUkRegFlags } from '../../hooks/useUkRegFlags/useUkRegFlags'
import { SupportedLocale } from '@afterpaytouch/shop-api'
import { CreateAccountRequest, registrationErrors, duplicateErrorCodeList, RegistrationStatus, RegistrationErrorCode } from '../../model/registration'
import {
  useAuthentication,
  useConsumerEmail,
  useConsumerMobile,
  useConsumerSelector,
  updateRegistrationData,
  updateConsumerMobile,
  useAppDispatch,
  useRegistration,
  useFinaliseAccountDetailsMutation,
  updateRegistrationStatus,
  useRegistrationConsumerGivenName,
  useRegistrationConsumerSurname,
  useRegistrationConsumerAddress,
  useRegistrationConsumerDateOfBirth,
} from '../../state'
import { ProfileCard } from '../ProfileCard'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { getCountryCodeFromLocale } from '../../utils/consumer'
import { MINIMUM_AGE_RESTRICTION } from '../VerifyChallenge/helper'
import { useIdFlowErrorHandler } from '../IDFlow'
import { SignupFormData } from './types'
import type { AmplitudeEventTypesProperties } from '../../integrations/amplitude/types'

export const getEnteredPrefillEvent = (attr: string): keyof AmplitudeEventTypesProperties =>
  `Entered Profile Confirm Page ${attr} Field` as unknown as keyof AmplitudeEventTypesProperties

export const getRequest = (
  data: SignupFormData,
  initialFormData: SignupFormData,
  preferredLocale = 'en-AU' as SupportedLocale,
  creditCheckAgree: boolean
): CreateAccountRequest => {
  const { givenNames, otherGivenNames, surname, dateOfBirth, mobile, email, address, marketingOptIn } = data
  return {
    ...address,
    countryCode: address.countryCode as Country,
    dateOfBirth,
    givenNames: givenNames ?? initialFormData.givenNames,
    mobile,
    email: email ?? initialFormData.email,
    otherGivenNames: otherGivenNames ?? initialFormData.otherGivenNames,
    surname: surname ?? initialFormData.surname,
    preferredLocale,
    creditCheckAgree,
    marketingOptIn,
  }
}

export const SignupForm: FunctionComponent = (): JSX.Element => {
  const signUpTerms = useSignUpTerms()
  const { t } = useTranslation()
  const givenNames = useRegistrationConsumerGivenName()
  const surname = useRegistrationConsumerSurname()
  const registrationAddress = useRegistrationConsumerAddress()
  const dateOfBirth = useRegistrationConsumerDateOfBirth()
  const [finaliseAccountDetails] = useFinaliseAccountDetailsMutation()
  // Keep consumer hooks for submit logic
  const phoneNumber = useConsumerMobile()
  const auth = useAuthentication()

  const supportedCountries = [Country.AU]
  const { locale, push } = useRouter()
  const country = getCountryCodeFromLocale(locale)
  const { parseMobile } = usePhoneNumber({ country })
  const consumerState = useConsumerSelector()

  // Merge consumer state with registration data for prefill
  const consumer = {
    ...consumerState,
    givenNames: givenNames ?? consumerState.givenNames,
    surname: surname ?? consumerState.surname,
    dateOfBirth: dateOfBirth ?? consumerState.dateOfBirth,
    contactAddress:
      registrationAddress?.address1?.length > 0 || registrationAddress?.postcode?.length > 0
        ? {
            id: consumerState.contactAddress?.id ?? 0,
            name: consumerState.contactAddress?.name ?? registrationAddress.address1,
            address1: registrationAddress.address1,
            address2: registrationAddress.address2 ?? null,
            area2: registrationAddress.area2 ?? null,
            suburb: registrationAddress.suburb,
            state: registrationAddress.state,
            postcode: registrationAddress.postcode,
            countryCode: consumerState.countryCode ?? country,
            addressType: consumerState.contactAddress?.addressType,
          }
        : consumerState.contactAddress,
  }
  const { error: reduxRegistrationError, status: registrationStatus } = useRegistration()
  const [localRegistrationError, setLocalRegistrationError] = useState<string | null>(null)

  // Use Redux error if available, otherwise fall back to local error
  const registrationError = reduxRegistrationError?.error ?? localRegistrationError
  const isDuplicatePhoneNumberError =
    registrationStatus === RegistrationStatus.Error &&
    reduxRegistrationError?.error !== undefined &&
    duplicateErrorCodeList.includes(reduxRegistrationError.error)
  const enableEditName = true
  const isMobileLogin = false
  const isAuthRequired = auth.verificationRequired
  const [isSubmitted, setIsSubmitted] = useState(false)
  const initialAddresses: ConsumerAddress | undefined =
    consumer.contactAddress != null
      ? {
          id: consumer.contactAddress.id,
          name: consumer.contactAddress.name,
          address1: consumer.contactAddress.address1 ?? '',
          address2: consumer.contactAddress.address2 ?? '',
          area2: consumer.contactAddress.area2 ?? '',
          suburb: consumer.contactAddress.suburb ?? '',
          state: consumer.contactAddress.state ?? '',
          postcode: consumer.contactAddress.postcode ?? '',
          countryCode: consumer.contactAddress.countryCode ?? consumer.countryCode ?? country,
          addressType: consumer.contactAddress.addressType,
        }
      : undefined
  const email = useConsumerEmail() ?? ''
  const dispatch = useAppDispatch()
  const isStateEligibleForPayByInstallmentEnabled = useFlag(LaunchDarklyFlags.REGISTRATION_STATE_VALIDATION)
  const { isUkMarketRegsFCA } = useUkRegFlags()

  const defaultMobile = parseMobile(phoneNumber) ?? ''
  const isCashTheme = useIsTheme(Theme.Cash)
  const [isAddressPrefilled, setIsAddressPrefilled] = useState<boolean | null>(null)
  const countryMinimumAgeRestriction = MINIMUM_AGE_RESTRICTION[country] ?? MINIMUM_AGE_RESTRICTION.DEFAULT_COUNTRY
  const addressState = Array.isArray(initialAddresses) ? initialAddresses[0]?.state : initialAddresses?.state
  const initialMinAge = countryMinimumAgeRestriction[addressState ?? ''] ?? countryMinimumAgeRestriction.DEFAULT
  const [minAge, setMinAge] = useState<number>(initialMinAge)
  const isNzCreditCheckEnabled = country === Country.NZ
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { handleError: handleIdFlowError } = useIdFlowErrorHandler()
  const { redirectAfterRegistration } = usePostRegistrationRedirect()
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState({
    givenNames: false,
    otherGivenNames: false,
    surname: false,
    dateOfBirth: false,
    mobile: false,
    address: false,
  })

  const { schema, initialFormData } = useMemo(() => {
    const components = {
      terms: () => <Disclaimer />,
      continueButton: () => {
        return (
          <Button.Primary
            onClick={() => {
              logEvent(TrackingEvent.PRESSED_CONTINUE_ON_PROFILE_CONFIRM_PAGE)
            }}
            loading={isSubmitted}
            type='submit'
            testNameSpace='sign-up-submit'
            disabled={isSubmitted}
            padding='Fluid'
            size='Regular'
          >
            {t('common:continue')}
          </Button.Primary>
        )
      },

      inlineError: () => {
        return (
          <Text color='Fire' size='XS' testNameSpace='sign-up-error'>
            <Trans i18nKey={`signUp:error.${registrationError as string}`} />
          </Text>
        )
      },
    }
    const handleGeocodeResult = (success: boolean): void => {
      setIsAddressPrefilled(success)
    }
    return getSchema({
      components,
      t,
      givenNames,
      surname,
      enableEditName,
      consumer,
      locale: (locale ?? 'en-AU') as SupportedLocale,
      country,
      minAge,
      defaultMobile,
      isMobileLogin,
      isAuthRequired,
      parseMobile,
      supportedPrefixes: supportedCountries,
      initialAddresses,
      isStateEligibleForPayByInstallmentEnabled,
      handleGeocodeResult,
      showInlineError: registrationError !== null,
      logEvent,
      isCashTheme,
    })
  }, [
    enableEditName,
    consumer,
    country,
    minAge,
    defaultMobile,
    initialAddresses,
    isStateEligibleForPayByInstallmentEnabled,
    enableEditName,
    isAddressPrefilled,
    givenNames,
    isNzCreditCheckEnabled,
    isSubmitted,
    surname,
    dateOfBirth,
    registrationAddress,
    isAuthRequired,
    minAge,
    registrationError,
  ])

  const [formData, setFormData] = useState(initialFormData)
  const preFilledInputNames = useRef(isValidObject(initialFormData) ? Object.keys(initialFormData).filter((key) => initialFormData[key]?.length > 0) : [])
  const creditCheckAgree = (consumer?.countryCode ?? country) !== Country.CA

  const doCreateAccount = async (data: SignupFormData): Promise<void> => {
    const request: CreateAccountRequest = getRequest(data, initialFormData, (locale ?? 'en-AU') as SupportedLocale, creditCheckAgree)
    try {
      if (isAuthRequired) {
        // SMS verification required - store data and route to verification
        await dispatch(updateRegistrationData(request))
        await dispatch(updateConsumerMobile(data.mobile))
        dispatch(updateRegistrationStatus(RegistrationStatus.Created))
        push(Route.VERIFY_SMS)
      } else {
        // No verification required - finalize account directly
        await dispatch(updateRegistrationData(request))
        await dispatch(updateConsumerMobile(data.mobile))

        await finaliseAccountDetails({
          ...request,
          countryCode: consumer?.countryCode,
          creditPolicyAgree: false,
          providedCreditCheckConsent: isNzCreditCheckEnabled,
        }).unwrap()

        await redirectAfterRegistration(consumer?.countryCode)
      }
    } catch (error) {
      dispatch(updateRegistrationStatus(RegistrationStatus.Error))
      const errorCode = error.data?.errorCode ?? 'default'
      setLocalRegistrationError(registrationErrors[errorCode] ?? RegistrationErrorCode.Default)
      handleAccountDetailsErrors(error, handleIdFlowError)
    } finally {
      setIsSubmitted(false)
    }
  }

  const handleOnChange = (data: SignupFormData): void => {
    setLocalRegistrationError(null)
    if (!compareObjects(data.address, formData.address, false, [], true)) {
      const updatedMinAge = countryMinimumAgeRestriction[data.address.state ?? ''] ?? countryMinimumAgeRestriction.DEFAULT
      setMinAge(updatedMinAge)
    }
    setFormData(data)
    SignupFormAnalyticsFields.forEach((key) => {
      if (!(isMobileLogin && key === 'email')) {
        if (valueIsNotNullOrUndefined(data[key]) && valueIsNotNullOrUndefined(formData[key]) && data[key] !== formData[key] && !hasSentAnalyticsData[key]) {
          const wasPrefilled = preFilledInputNames.current.includes(key)
          logEvent(getEnteredPrefillEvent(FieldAnalyticsLabels[key]), { wasPrefilled: !!wasPrefilled })
          setHasSentAnalyticsData({ ...hasSentAnalyticsData, [key]: true })
        }
      }
      if (isAddressPrefilled !== null) {
        if (!compareObjects(data.address, formData.address, false, [], true) && !hasSentAnalyticsData.address) {
          const wasPrefilled = preFilledInputNames.current.includes('address')
          logEvent(getEnteredPrefillEvent(FieldAnalyticsLabels.address), { wasPrefilled: !!wasPrefilled })
          setHasSentAnalyticsData({ ...hasSentAnalyticsData, address: true })
        }
      }
    })
  }

  const handleOnSubmit = async (data: SignupFormData): Promise<void> => {
    setIsSubmitted(true)
    await doCreateAccount(data)
  }

  const handleStateChange = (isValid: boolean, errors): void => {
    if (!isValid) {
      setIsSubmitted(false)
    }
  }

  const renderDisclaimer = (): JSX.Element => {
    const disclaimer = isUkMarketRegsFCA ? 'signUp:disclaimerFCA' : 'signUp:disclaimer'
    return (
      <Text size='XS'>
        <Message iconName='Info' description={<Trans i18nKey={disclaimer} components={signUpTerms} />} />
      </Text>
    )
  }

  useEffect(() => {
    if (isAddressPrefilled !== null && !preFilledInputNames.current.includes('address')) {
      preFilledInputNames.current = [...preFilledInputNames.current, ...(isAddressPrefilled ? ['address'] : [])]
    }
  }, [isAddressPrefilled])

  useEffect(() => {
    if (isDuplicatePhoneNumberError) {
      logEvent(TrackingEvent.VIEWED_EXISTING_MOBILE_NUMBER_ERROR)
    }
  }, [isDuplicatePhoneNumberError, logEvent])

  return (
    <>
      <div className='mb-4 mt-4'>
        <ProfileCard consumerName={''} consumerEmail={email ?? ''} />
      </div>
      <div className='mb-2'>
        <Text size='M'>{t('signUp:description')}</Text>
      </div>
      <FormBuilder schema={schema} testNameSpace={'sign-up'} onSubmit={handleOnSubmit} onChange={handleOnChange} onStateChange={handleStateChange} />
      {country === Country.GB && renderDisclaimer()}
    </>
  )
}
